"use client";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Loader2, AlertTriangle, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import useRendezvous from "../../../../appointments/hooks/useRendezvous";
import useDoctors from "../../../../authDoctor/hooks/useDoctors";
import "./AppointmentSummary.css";

/**
 * Props:
 *  - patientId?: string|number (facultatif; si absent, résolu depuis le store)
 */
export default function AppointmentSummary({ patientId }) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const mounted = useRef(true);

  const { getUpcomingPatientAppointments } = useRendezvous();
  const { doctors, loading: doctorsLoading } = useDoctors();

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const items = await getUpcomingPatientAppointments(patientId);
      if (mounted.current) setAppointments(Array.isArray(items) ? items : []);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        t("appointmentSummary.errors.loadFailed");
      if (mounted.current) {
        setErr(msg);
        setAppointments([]);
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [getUpcomingPatientAppointments, patientId, t]);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  // ---- Jointure RDV ↔ Docteurs ----
  const pickDoctorFields = (d) => ({
    id: d?.id ?? d?.doctorId ?? d?.medecinId ?? d?.userId ?? d?._id ?? d?.pgId,
    name:
      d?.fullName ??
      d?.name ??
      d?.displayName ??
      d?.doctorName ??
      d?.username,
    specialty: d?.specialty ?? d?.speciality ?? d?.specialite ?? d?.title ?? "",
  });

  const doctorById = useMemo(() => {
    const map = new Map();
    (doctors || []).forEach((d) => {
      const f = pickDoctorFields(d);
      if (f.id != null) map.set(String(f.id), f);
    });
    return map;
  }, [doctors]);

  const enriched = useMemo(() => {
    return (appointments || []).map((a) => {
      const key = String(a.medecinId ?? a.doctorId ?? "");
      const doc = key ? doctorById.get(key) : null;

      return {
        ...a,
        providerDisplay:
          doc?.name ||
          a.providerDisplay ||
          a.provider ||
          t("appointmentSummary.fallbacks.doctor"),
        specialtyDisplay:
          doc?.specialty ||
          a.specialtyDisplay ||
          a.specialty ||
          "",
      };
    });
  }, [appointments, doctorById, t]);

  return (
    <div className="appointment-summary-card">
      <h3 className="summary-title">
        <span aria-hidden>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M6.667 1.667V5.000"
              stroke="#1e1e1e"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.333 1.667V5.000"
              stroke="#1e1e1e"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15.833 3.333H4.167A1.667 1.667 0 0 0 2.5 5v11.667A1.667 1.667 0 0 0 4.167 18.333h11.666A1.667 1.667 0 0 0 17.5 16.667V5A1.667 1.667 0 0 0 15.833 3.333Z"
              stroke="#1e1e1e"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2.5 8.333H17.5"
              stroke="#1e1e1e"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        {t("appointmentSummary.title")}
      </h3>

      {(loading || doctorsLoading) && (
        <div className="summary-loading">
          <Loader2 className="spin" size={18} />
          <span>{t("appointmentSummary.loading")}</span>
        </div>
      )}

      {!loading && !doctorsLoading && err && (
        <div className="summary-error">
          <AlertTriangle size={16} />
          <span>{err}</span>
        </div>
      )}

      {!loading && !doctorsLoading && enriched.length === 0 && (
        <div className="summary-empty">
          <CheckCircle size={16} />
          <span>{t("appointmentSummary.empty")}</span>
        </div>
      )}

      {!loading && !doctorsLoading && enriched.length > 0 && (
        <div className="summary-list">
          {enriched.map((a) => (
            <div key={a.id} className="summary-item">
              <span className="summary-dot" aria-hidden="true" />
              <div className="summary-content">
                <p className="summary-time">
                  {a._display?.dateText ||
                    a.time ||
                    t("appointmentSummary.fallbacks.dateNotAvailable")}
                </p>

                {(a._display?.timeText || a.duration) && (
                  <p className="summary-duration">
                    {a._display?.timeText || a.duration}
                  </p>
                )}

                <p className="summary-provider">
                  {a.providerDisplay}
                  {a.specialtyDisplay ? `, ${a.specialtyDisplay}` : ""}
                </p>
              </div>

              {a.status && (
                <span className="summary-status neutral">
                  {t("appointmentSummary.statusPrefix", {
                    status: String(a.status).toLowerCase(),
                  })}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
